import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, expand, Observable, scan } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiConstants } from '../constants/api-constant';
import { environment } from '../../../environments/environment';
import { EventDataIteration, GlobalData } from '../models/eventDetails';
import { GlobalChartSummary, RedZoneTelemetryResponse, WorkFlow } from '../models/global-view';
import { riskStatus } from '../models/dashabordEventDetails';
import { Equipment } from '../models/equipment';
import { RigProfile } from '../models/zoneSetting';

@Injectable({
  providedIn: 'root',
})
export class GlobalViewService {
  public bookmarksEnabled = new BehaviorSubject<string>('');
  public bookmarksToggleSelection = this.bookmarksEnabled.asObservable();

  private baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.apiBaseUrl;
  }

  public getWorkFlowId(): Observable<WorkFlow[]> {
    return this.http.get<WorkFlow[]>(`${this.baseUrl}${ApiConstants.BASEURL}WorkFlow`);
  }

  public getSiteList(countries: string[]): Observable<WorkFlow[]> {
    const param = {
      countries,
    };

    return this.http.post<WorkFlow[]>(`${this.baseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}asset-list`, param);
  }

  public getRiskList(): Observable<riskStatus[]> {
    return this.http.get<riskStatus[]>(`${this.baseUrl}${ApiConstants.BASEURL}${ApiConstants.EVENT}get-risk-categories`);
  }

  public getEventChart(
    startDate: string,
    endDate: string,
    timeZone: string,
    filterData: { [key: string]: string[] | boolean },
    showAllEvents: string
  ): Observable<GlobalChartSummary> {
    const param = {
      startDate,
      endDate,
      timeZone,
      ...filterData,
      showAllEvents,
    };

    return this.http.post<GlobalChartSummary>(`${this.baseUrl}${ApiConstants.BASEURL}Event/get-event-chart`, param);
  }

  public getEventDetails(
    startDate: string,
    endDate: string,
    filterData: { [key: string]: string[] | boolean },
    showAllEvents: string,
    pageNo: number,
    records: number,
    timeZone?: string
  ): Observable<GlobalData> {
    const param = {
      startDate,
      endDate,
      ...filterData,
      showAllEvents,
      pageNo,
      records,
      timeZone,
    };

    return this.http.post<GlobalData>(`${this.baseUrl}${ApiConstants.BASEURL}Event/get-events-details`, param);
  }

  public getAllEventData(
    startDate: string,
    endDate: string,
    filterData: { [key: string]: string[] | boolean },
    showAllEvents: string,
    pageNo: number,
    records: number,
    timeZone?: string
  ): Observable<EventDataIteration> {
    let totalPages = 0;

    return this.getEventDetails(startDate, endDate, filterData, showAllEvents, pageNo, records, timeZone).pipe(
      expand((response: GlobalData) => {
        const dataResponse: GlobalData = JSON.parse(JSON.stringify(response));
        if (dataResponse.totalRecords && totalPages !== pageNo) {
          return this.getEventDetails(startDate, endDate, filterData, showAllEvents, ++pageNo, records, timeZone);
        } else {
          pageNo = 1;
          this.getEventDetails(startDate, endDate, filterData, showAllEvents, ++pageNo, records, timeZone);

          return EMPTY;
        }
      }),
      scan((accumulatedData: EventDataIteration, currentData: GlobalData, index: number) => {
        if (Object.keys(accumulatedData).length === 0) {
          totalPages = Math.round(currentData.totalRecords / records);
          totalPages = currentData.totalRecords % records ? ++totalPages : totalPages;
          accumulatedData = {
            ...currentData,
            index,
            totalPages,
          };
        } else {
          accumulatedData.data = [...currentData.data];
          accumulatedData.index = index;
        }

        return accumulatedData;
      }, {} as EventDataIteration)
    );
  }

  public getGatewayList(countries: string[]): Observable<WorkFlow[]> {
    const param = {
      countries,
    };

    return this.http.post<WorkFlow[]>(`${this.baseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}gateway-list`, param);
  }

  public getEquipmentList(): Observable<Equipment[]> {
    return this.http.get<Equipment[]>(`${this.baseUrl}${ApiConstants.BASEURL}dashboard/equipment-list`);
  }

  public getRigProfiles(): Observable<RigProfile[]> {
    return this.http.get<RigProfile[]>(`${this.baseUrl}${ApiConstants.BASEURL}${ApiConstants.DASHBOARD}profile-type-master`);
  }

  public getRedzoneTelemetryDetails(
    startDate: string,
    endDate: string,
    filterData: { [key: string]: string[] | boolean },
    showAllEvents: string,
    pageNo: number,
    records: number,
    timeZone: string
  ): Observable<RedZoneTelemetryResponse> {
    const param = {
      startDate,
      endDate,
      ...filterData,
      showAllEvents,
      pageNo,
      records,
      timeZone,
    };

    return this.http.post<RedZoneTelemetryResponse>(`${this.baseUrl}${ApiConstants.BASEURL}Event/people-kpi`, param);
  }

  public getRedzoneTelemetryByCycle(
    startDate: string,
    endDate: string,
    filterData: { [key: string]: string[] | boolean },
    showAllEvents: string,
    pageNo: number,
    records: number,
    timeZone: string
  ): Observable<RedZoneTelemetryResponse[]> {
    const param = {
      startDate,
      endDate,
      ...filterData,
      showAllEvents,
      pageNo,
      records,
      timeZone,
    };

    return this.http.post<RedZoneTelemetryResponse[]>(`${this.baseUrl}${ApiConstants.BASEURL}Event/people-kpi-by-cycle`, param);
  }

  public sendAnalyticsReportOverEmail(
    startDate: string,
    endDate: string,
    bookMarkOnly: boolean,
    filterData: { [key: string]: string[] | boolean },
    showAllEvents: string,
    pageNo: number,
    records: number,
    timeZone?: string
  ): Observable<GlobalData> {
    const param = {
      startDate,
      endDate,
      bookMarkOnly,
      ...filterData,
      showAllEvents,
      pageNo,
      records,
      timeZone,
    };

    return this.http.post<GlobalData>(`${this.baseUrl}${ApiConstants.BASEURL}Event/send-analytics-email`, param);
  }

  public toggleBookmark(eventId: number): Observable<string> {
    return this.http.post<string>(`${this.baseUrl}${ApiConstants.BASEURL}Event/toggle-event-bookmark/${eventId}`, {});
  }

  public refreshAnalyticsWidgets(value: string): void {
    this.bookmarksEnabled.next(value);
  }
}
